
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserRole } from "@/types/user";

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultEmail?: string;
  defaultRole?: UserRole;
}

const formSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  title: z.string().optional(),
  company: z.string().optional(),
  role: z.enum(["admin", "instructor", "learner"]),
});

type FormValues = z.infer<typeof formSchema>;

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  defaultEmail = "",
  defaultRole = "learner",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.log("Dialog open state:", open); // Debug log
  console.log("Default email:", defaultEmail); // Debug log
  console.log("Default role:", defaultRole); // Debug log

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: defaultEmail,
      password: "",
      firstName: "",
      lastName: "",
      title: "",
      company: "",
      role: defaultRole,
    },
  });

  // Update form values when props change
  useEffect(() => {
    if (open) {
      console.log("Setting form values"); // Debug log
      form.setValue('email', defaultEmail);
      form.setValue('role', defaultRole);
    }
  }, [defaultEmail, defaultRole, open, form]);

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      console.log("Resetting form"); // Debug log
      form.reset({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        title: "",
        company: "",
        role: "learner",
      });
    }
  }, [open, form]);

  const onSubmit = async (values: FormValues) => {
    console.log("Form submitted:", values); // Debug log
    
    try {
      setIsSubmitting(true);
      
      // Sign up the user with the provided details
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            title: values.title,
            company: values.company,
            role: values.role,
          },
        },
      });

      if (authError) {
        console.error("Auth error:", authError); // Debug log
        
        // Check if it's a "User already registered" error
        if (authError.message?.includes("already registered")) {
          toast({
            title: "User Already Exists",
            description: "This email is already registered. Please use the 'Update to Admin' option instead.",
            variant: "destructive",
          });
          onClose();
          return;
        }
        
        throw authError;
      }
      
      if (!authData.user) {
        console.error("No user returned"); // Debug log
        throw new Error("Failed to create user");
      }
      
      console.log("User created:", authData.user.id); // Debug log
      
      // The user profile should be automatically created by the database trigger
      // Let's ensure the role, title and company are set correctly
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          role: values.role,
          title: values.title || null,
          company: values.company || null,
          first_name: values.firstName,
          last_name: values.lastName,
        })
        .eq("id", authData.user.id);

      if (profileError) {
        console.error("Profile error:", profileError); // Debug log
        throw profileError;
      }
      
      toast({
        title: "User Created",
        description: `Successfully created user: ${values.firstName} ${values.lastName}`,
      });
      
      form.reset();
      onSuccess();
    } catch (error: any) {
      console.error("Error creating user:", error);
      
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Create a new user account with specific role and permissions.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email*</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password*</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Product Manager" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corp" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role*</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="learner">Learner</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
